import { useTranslations } from 'next-intl'
import { PropsWithChildren, ReactNode } from 'react'

import { cn } from '@/shared/lib'
import { Icon } from '@/shared/ui'

type TitleProps = {
  warning?: boolean
  hasIcon?: boolean
  className?: string
  children?: ReactNode
}

export const Title = ({
  warning = false,
  hasIcon = true,
  className,
  children,
}: TitleProps) => {
  const t = useTranslations('common.notice')

  const title = children ?? t('title')

  if (hasIcon) {
    return (
      <h4 className={cn('mb-2 flex items-center font-semibold', className)}>
        <Icon
          name="alert-filled"
          size={24}
          className={cn('text-black', warning && 'text-red-500')}
        />
        <span
          className={cn('typo-body2 text-black', warning && 'text-red-500')}
        >
          {title}
        </span>
      </h4>
    )
  }

  return (
    <h4
      className={cn(
        'typo-label1 mb-2.5 font-semibold text-gray-900',
        warning && 'text-red-500',
        className,
      )}
    >
      {title}
    </h4>
  )
}

type ListProps = {
  gap?: boolean
  className?: string
}

export const List = ({
  gap = true,
  className,
  children,
}: PropsWithChildren<ListProps>) => {
  return (
    <ul
      className={cn(
        'typo-label2 relative space-y-2 text-start font-normal text-gray-500',
        '*:relative *:before:absolute *:before:top-[7px] *:before:left-0 *:before:h-0.5 *:before:w-0.5 *:before:rounded-full *:before:bg-gray-500',
        gap && '*:pl-3.5 *:before:ml-2',
        !gap && '*:pl-1.5',
        className,
      )}
    >
      {children}
    </ul>
  )
}

type ItemProps = {
  className?: string
}

export const Item = ({ className, children }: PropsWithChildren<ItemProps>) => {
  return (
    <li>
      <span className={cn('block whitespace-break-spaces', className)}>
        {children}
      </span>
    </li>
  )
}
